import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { usePostHog } from "posthog-js/react";
import { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, Send, RefreshCw } from "lucide-react";

const GamerGuideChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const posthog = usePostHog();

  // Load messages from localStorage on component mount
  useEffect(() => {
    const savedMessages = localStorage.getItem("chatbotMessages");
    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
    } else {
      // Add welcome message if no saved messages
      setMessages([
        {
          role: "bot",
          content:
            "👋 Hey there, gamer! I'm GG, your GameAway guide. How can I help you level up your gaming experience today?",
          timestamp: new Date().toISOString(),
        },
      ]);
    }
  }, []);

  // Save messages to localStorage when they change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem("chatbotMessages", JSON.stringify(messages));
    }
  }, [messages]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when chat is opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: "user",
      content: inputMessage,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        "https://q9p448rog2.execute-api.ap-south-1.amazonaws.com/default/getGamerGuideResponse-dev-hello",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId: posthog.get_distinct_id(),
            messages: [...messages, userMessage],
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Server responded with ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();

      const result = data.response.includes('</think>')
        ? data.response.substring(data.response.indexOf('</think>') + 8)
        : data.response;

      const botMessage = {
        role: "bot",
        content: result,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("Error:", error);
      setError(error.message);
      const errorMessage = {
        role: "bot",
        content: `Sorry, I encountered an error: ${error.message}. Would you like to try again?`,
        timestamp: new Date().toISOString(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const clearChat = () => {
    setMessages([
      {
        role: "bot",
        content:
          "👋 Hey there, gamer! I'm GG, your GameAway Guide. How can I help you level up your gaming experience today?",
        timestamp: new Date().toISOString(),
      },
    ]);
    localStorage.removeItem("chatbotMessages");
  };

  return (
    <div className="fixed bottom-8 right-8 z-10">
      {!isOpen ? (
        <Button className="rounded-full" onClick={() => setIsOpen(true)}>
          Have a question? Ask GG
        </Button>
      ) : (
        <Card className="w-full max-w-[800px] h-[400px] flex flex-col rounded-xl border-[0.5px] border-[var(--border-tertiary)] bg-background">
          <div className="p-2 border-b border-[var(--border-tertiary)] flex justify-between items-center">
            <h3 className="font-semibold">
              <Badge
                variant="secondary"
                className="bg-fuchsia-900 text-fuchsia-200 mr-2"
              >
                BETA
              </Badge>
              AI Gamer Guide
            </h3>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={clearChat}>
                Clear Chat
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
              >
                Close
              </Button>
            </div>
          </div>

          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 ${message.role === "user" ? "justify-end" : "justify-start"
                    }`}
                >
                  <Avatar>
                    <AvatarFallback>
                      {message.role === "user" ? "U" : "GG"}
                    </AvatarFallback>
                  </Avatar>
                  <div
                    className={`rounded-lg p-3 max-w-[80%] ${message.role === "user"
                      ? "bg-primary text-white"
                      : "bg-muted"
                      }`}
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      className={`prose ${message.role === "user"
                        ? "prose-p:text-neutral-200 text-neutral-200"
                        : "prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-white prose-a:text-white prose-th:text-white text-muted-foreground"
                        } prose-sm max-w-none`}
                    >
                      {message.content}
                    </ReactMarkdown>
                    <div className="text-xs mt-1 opacity-70 text-right">
                      {formatTimestamp(message.timestamp)}
                    </div>
                    {message.isError && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2 w-full"
                        onClick={() => {
                          setMessages((prev) =>
                            prev.filter((_, i) => i !== index)
                          );
                          setInputMessage(messages[index - 1]?.content || "");
                        }}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" /> Retry
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex items-start gap-3 justify-start">
                  <Avatar>
                    <AvatarFallback>GG</AvatarFallback>
                  </Avatar>
                  <div className="rounded-lg bg-muted p-3 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Thinking...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          <div className="p-4">
            <form className="flex gap-2" onSubmit={handleSubmit}>
              <Input
                ref={inputRef}
                placeholder="Type your message..."
                className="flex-1"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
              />
              <Button
                type="submit"
                disabled={isLoading || !inputMessage.trim()}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </form>
          </div>
        </Card>
      )}
    </div>
  );
};

export default GamerGuideChatbot;
